import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { usePageNameContext } from "../../../contexts/PageTitleContext";
import usePartPro from "../../../data/usePartPro";
import usePartProTrade from "../../../data/usePartProTrade";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import LoadingWheel from "../../../components/shared/LoadingWheel";
import { ButtonInput } from "../../../components/shared/Inputs/ButtonInput";
import classNames from "../../../utils/classNames";
import {
  buildCategoryUpdate,
  clearShown,
  filterCategories,
  selectAllShown,
  selectionEquals,
  selectionFromSettings,
  toggleDefault,
  toggleOffered,
  TradeCategorySelection,
} from "../../../utils/partProTradeCategories";

const MIN_BID_WINDOW_HOURS = 1;
const MAX_BID_WINDOW_HOURS = 720;

// Manages what this yard offers on PartPro's trade (bidding) portal and the bidding
// defaults. Vehicles, buyers and awards stay on the portal itself.
export default function PartProTradeSettings() {
  const { setInfo } = usePageNameContext();
  const { connection } = usePartPro();
  const connected = !!connection.data?.data?.connected;
  const { settings, updateSettings } = usePartProTrade(connected);
  const { saveHandlers } = useDefaultCRUDHandlers("PartPro trade settings");

  const [publicProfile, setPublicProfile] = useState(false);
  const [bidWindowHours, setBidWindowHours] = useState("72");
  const [query, setQuery] = useState("");
  const [selection, setSelection] = useState<TradeCategorySelection>({ offered: new Set(), defaults: new Set() });

  useEffect(() => {
    setInfo({
      name: "PartPro Trade",
      desc: "Choose which bid categories you offer on PartPro's trade portal",
    });
  }, []);

  const data = settings.data?.data;
  const categories = data?.categories ?? [];

  // Re-seed the form whenever PartPro answers - it is the source of truth after a save too
  useEffect(() => {
    if (!data) return;
    setPublicProfile(data.publicProfile);
    setBidWindowHours(String(data.defaultBidWindowHours));
    setSelection(selectionFromSettings(data.categories));
  }, [data]);

  const shown = useMemo(() => filterCategories(categories, query), [categories, query]);
  const shownIds = useMemo(() => new Set(shown.map((c) => c.id)), [shown]);

  const parsedHours = Number(bidWindowHours);
  const hoursValid =
    Number.isInteger(parsedHours) && parsedHours >= MIN_BID_WINDOW_HOURS && parsedHours <= MAX_BID_WINDOW_HOURS;

  const dirty =
    !!data &&
    (publicProfile !== data.publicProfile ||
      parsedHours !== data.defaultBidWindowHours ||
      !selectionEquals(selection, selectionFromSettings(data.categories)));

  const handleSave = () => {
    if (!hoursValid) return;
    updateSettings.mutate(
      {
        publicProfile,
        defaultBidWindowHours: parsedHours,
        categories: buildCategoryUpdate(categories, selection),
      },
      saveHandlers
    );
  };

  if (connection.isLoading || (connected && settings.isLoading)) {
    return <LoadingWheel />;
  }

  if (!connected) {
    return (
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900">PartPro is not connected</h2>
        <p className="mt-1 text-sm text-gray-500">
          Connect PartPro under{" "}
          <Link to="/admin/connections" className="text-primary-600 hover:text-primary-700 underline">
            Auth Connections
          </Link>{" "}
          to manage your trade categories from here.
        </p>
      </section>
    );
  }

  if (settings.isError || !data) {
    return (
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900">PartPro trade settings unavailable</h2>
        <p className="mt-1 text-sm text-gray-500">
          {settings.error instanceof Error ? settings.error.message : "PartPro did not return your trade settings."}
        </p>
      </section>
    );
  }

  const offeredCount = selection.offered.size;

  return (
    <div className="space-y-8">
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900">Bidding defaults</h2>
        <p className="mt-1 text-sm text-gray-500">
          Vehicles are pushed to the trade portal automatically once a collection is scheduled and open for
          bidding on every category you offer below.
        </p>

        <div className="mt-4 grid gap-6 sm:grid-cols-3">
          <div>
            <label htmlFor="bidWindowHours" className="block text-sm font-medium text-gray-700">
              Default bidding window (hours)
            </label>
            <input
              id="bidWindowHours"
              type="number"
              min={MIN_BID_WINDOW_HOURS}
              max={MAX_BID_WINDOW_HOURS}
              value={bidWindowHours}
              onChange={(e) => setBidWindowHours(e.target.value)}
              className={classNames(
                "mt-1 block w-32 rounded-md shadow-sm sm:text-sm",
                hoursValid ? "border-gray-300 focus:border-primary-500 focus:ring-primary-500" : "border-red-400"
              )}
            />
            <p className="mt-1 text-xs text-gray-500">
              {hoursValid ? "72 = three days. Applied to each vehicle when it opens." : `Enter ${MIN_BID_WINDOW_HOURS}-${MAX_BID_WINDOW_HOURS} hours.`}
            </p>
          </div>

          <div>
            <span className="block text-sm font-medium text-gray-700">Public trade profile</span>
            <label className="mt-2 flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={publicProfile}
                onChange={(e) => setPublicProfile(e.target.checked)}
                className="h-5 w-5"
              />
              Listed on the portal so buyers can request access
            </label>
            <p className="mt-1 text-xs text-gray-500">When off, buyers only join by your invitation.</p>
          </div>

          <div>
            <span className="block text-sm font-medium text-gray-700">PartPro commission</span>
            <p className="mt-2 text-sm text-gray-900">{data.tradeCommissionPct}% of awarded bids</p>
            <p className="mt-1 text-xs text-gray-500">Set by PartPro - contact them to discuss.</p>
          </div>
        </div>
      </section>

      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900">Categories you offer</h2>
        <p className="mt-1 text-sm text-gray-500">
          The list mirrors Wilma's part categories. Tick the ones your buyers can bid on; only these appear on
          your vehicles. &ldquo;Default for new buyers&rdquo; grants the category to every new buyer automatically.
        </p>

        {offeredCount === 0 && (
          <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            No categories offered. Vehicles pushed to PartPro trade will have nothing to bid on and cannot open
            until you tick at least one.
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search categories"
            aria-label="Search categories"
            className="block w-64 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          />
          <ButtonInput label="Select all shown" onClick={() => setSelection((s) => selectAllShown(s, shown))} classes={undefined} size="sm" />
          <ButtonInput label="Clear shown" onClick={() => setSelection((s) => clearShown(s, shown))} classes={undefined} size="sm" />
          <span className="text-sm text-gray-500">
            <strong className="text-gray-900">{offeredCount}</strong> of {categories.length} offered,{" "}
            <strong className="text-gray-900">{selection.defaults.size}</strong> default for new buyers
            {query.trim() && <>, showing {shown.length}</>}
          </span>
        </div>

        <div className="mt-4 max-h-[32rem] overflow-y-auto rounded-md border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="sticky top-0 bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-500">Offer</th>
                <th className="px-3 py-2 text-left font-medium text-gray-500">Category</th>
                <th className="px-3 py-2 text-left font-medium text-gray-500">Default for new buyers</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {categories.map((c) => {
                const isOffered = selection.offered.has(c.id);
                return (
                  <tr key={c.id} className={classNames(!shownIds.has(c.id) ? "hidden" : "", isOffered ? "bg-primary-50" : "")}>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        aria-label={`Offer ${c.name}`}
                        checked={isOffered}
                        onChange={(e) => setSelection((s) => toggleOffered(s, c.id, e.target.checked))}
                        className="h-5 w-5"
                      />
                    </td>
                    <td className="px-3 py-2 text-gray-900">
                      {c.name}
                      {!c.externalId && <span className="ml-2 text-xs text-gray-400">PartPro custom</span>}
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        aria-label={`Default ${c.name} for new buyers`}
                        checked={selection.defaults.has(c.id)}
                        onChange={(e) => setSelection((s) => toggleDefault(s, c.id, e.target.checked))}
                        className="h-5 w-5"
                      />
                    </td>
                  </tr>
                );
              })}
              {shown.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-3 py-6 text-center text-gray-500">
                    {categories.length === 0
                      ? "PartPro has no trade categories yet - they arrive with the nightly Wilma sync."
                      : "No categories match that search."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <div className="sticky bottom-0 flex items-center gap-3 border-t border-gray-200 bg-white/95 py-3 backdrop-blur">
        <ButtonInput
          label={updateSettings.isLoading ? "Saving…" : "Save to PartPro"}
          onClick={handleSave}
          disabled={!dirty || !hoursValid}
          isLoading={updateSettings.isLoading}
          classes={undefined}
        />
        {dirty && <span className="text-sm text-gray-500">Unsaved changes</span>}
      </div>
    </div>
  );
}
